if (!customElements.get('image-text-blocks')) {
    customElements.define(
        'image-text-blocks',
        class extends HTMLElement {
            static TRANSITION_MS = 300;

            constructor() {
                super();

                this.lockedIndex = '0';
                this.hoveredIndex = null;

                this.canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

                this.elBlockToggles = this.querySelectorAll('[data-block]');
                this.elDescriptions = this.querySelectorAll('[data-description]');
                this.elImages = this.querySelectorAll('[data-image]');

                this.elBlockToggles.forEach((el) => {
                    el.addEventListener('click', this.toggleBlock.bind(this));
                    el.addEventListener('focus', this.onBlockActivate.bind(this));
                    el.addEventListener('blur', this.onBlockDeactivate.bind(this));

                    if (this.canHover) {
                        el.addEventListener('mouseenter', this.onBlockActivate.bind(this));
                        el.addEventListener('mouseleave', this.onBlockDeactivate.bind(this));
                    }
                });
            }

            connectedCallback() {
                this.elImages.forEach((el) => this.waitForImage(el));
                this.addEventListener('video:loaded', this.onVideoLoaded.bind(this));
            }

            onVideoLoaded({ target }) {
                if (target.closest('[data-image]')?.classList.contains('is-active')) return;
                target.pauseVideo?.();
            }

            onBlockActivate({ currentTarget }) {
                this.hoveredIndex = currentTarget.dataset.block;
                this.setActiveIndex(this.hoveredIndex);
            }

            onBlockDeactivate() {
                this.hoveredIndex = null;
                this.setActiveIndex(this.lockedIndex);
            }

            waitForImage(imageItem) {
                const img = imageItem?.querySelector('img');
                if (!img) return Promise.resolve();

                if (img.complete && img.naturalWidth > 0) return Promise.resolve();

                return new Promise((resolve) => {
                    const done = () => resolve();
                    img.addEventListener('load', done, { once: true });
                    img.addEventListener('error', done, { once: true });

                    const src = img.currentSrc || img.src;
                    if (src) {
                        const preload = new Image();
                        preload.addEventListener('load', done, { once: true });
                        preload.addEventListener('error', done, { once: true });
                        preload.src = src;
                    }
                });
            }

            toggleBlock({ currentTarget }) {
                const blockIndex = currentTarget.dataset.block;

                if (blockIndex === this.lockedIndex) return;

                this.lockedIndex = blockIndex;
                this.hoveredIndex = null;

                this.setActiveIndex(this.lockedIndex);
            }

            setActiveIndex(blockIndex) {
                this.elBlockToggles.forEach((el) => {
                    el.classList.toggle('active', el.dataset.block === blockIndex);
                });

                this.applyColors(blockIndex);
                this.switchDescription(blockIndex);

                this.activeIndex = blockIndex;

                const currentImage = this.querySelector('.image-text-blocks--image-item.is-active');
                const targetImage = this.querySelector(`[data-image="${blockIndex}"]`);

                if (!targetImage || currentImage === targetImage) return;

                this.waitForImage(targetImage).then(() => {
                    if (this.activeIndex !== blockIndex) return;

                    this.crossfadeImage(
                        this.querySelector('.image-text-blocks--image-item.is-active'),
                        targetImage,
                    );
                });
            }

            applyColors(blockIndex) {
                const toggle = this.querySelector(`[data-block="${blockIndex}"]`);
                if (!toggle?.dataset.colors) return;

                const blockColors = JSON.parse(toggle.dataset.colors.replaceAll("'", '"'));
                this.style.setProperty('--color-background', blockColors.background);
                this.style.setProperty('--color-text', blockColors.text);
                this.style.setProperty('--color-text-accent', blockColors.accent);
            }

            switchDescription(blockIndex) {
                clearTimeout(this.swapTimer);
                clearTimeout(this.settleTimer);

                const currentDescription = this.querySelector(
                    '.image-text-blocks--description:not([hidden])',
                );
                const targetDescription = this.querySelector(`[data-description="${blockIndex}"]`);

                if (!targetDescription || currentDescription === targetDescription) {
                    this.classList.remove('transitioning');
                    return;
                }

                this.classList.add('transitioning');

                this.swapTimer = setTimeout(() => {
                    this.elDescriptions.forEach((el) => {
                        el.toggleAttribute('hidden', el.dataset.description != blockIndex);
                    });

                    this.settleTimer = setTimeout(() => {
                        this.classList.remove('transitioning');
                    }, 100);
                }, this.constructor.TRANSITION_MS);
            }

            crossfadeImage(from, to) {
                to.removeAttribute('inert');
                to.classList.add('is-active');
                to.querySelector('video-component')?.playVideo?.(true);

                if (from) {
                    from.classList.remove('is-active');
                    from.setAttribute('inert', '');
                    from.querySelector('video-component')?.pauseVideo?.();
                }
            }
        },
    );
}
